import {
  draggable,
  dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import { Button, Icon, Label, Tooltip, token } from "@may-i/mash-design-system";
import { type ChangeEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";
import type { FloorPlan } from "~/entities/floor-plan/types";
import { IcDelete } from "~/shared/assets/icons/IcDelete";
import { TEMP_IMAGE_URL } from "~/shared/ui/EmptyImagePlaceholder";
import { ImageLightbox } from "./ImageLightbox";

// --- Types ---

export type DropIndicatorPosition = {
  targetIndex: number;
  edge: "left" | "right";
} | null;

interface FloorPlanCardProps {
  floorPlan: FloorPlan;
  deletable?: boolean;
  reordering?: boolean;
  index?: number;
  dropIndicator?: DropIndicatorPosition;
  isSaving?: boolean;
  onSave: (id: number, data: { name: string; imageFile?: File }) => void;
  onDelete: (id: number, name: string) => void;
}

export function FloorPlanCard({ floorPlan, deletable = true, reordering = false, index, dropIndicator, isSaving, onSave, onDelete }: FloorPlanCardProps) {
  const { t } = useTranslation("resource");
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(floorPlan.name);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);
  const [imageFile, setImageFile] = useState<File | undefined>(undefined);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!reordering || !cardRef.current) return;

    return combine(
      draggable({
        element: cardRef.current,
        getInitialData: () => ({ index, floorPlanId: floorPlan.id }),
        onDragStart: () => setIsDragging(true),
        onDrop: () => setIsDragging(false),
      }),
      dropTargetForElements({
        element: cardRef.current,
        getData: () => ({ index }),
      }),
    );
  }, [reordering, index, floorPlan.id]);

  const handleStartEdit = () => {
    setName(floorPlan.name);
    setPreviewUrl(undefined);
    setImageFile(undefined);
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
  };

  const handleSave = () => {
    onSave(floorPlan.id, { name, imageFile });
    setEditing(false);
  };

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const displayImageUrl = previewUrl ?? floorPlan.imageUrl;

  const showLeftIndicator = dropIndicator?.targetIndex === index && dropIndicator?.edge === "left";
  const showRightIndicator = dropIndicator?.targetIndex === index && dropIndicator?.edge === "right";

  if (editing) {
    return (
      <Card $editing>
        <ImageArea $editing>
          {displayImageUrl ? (
            <CardImage src={displayImageUrl} alt={name} />
          ) : null}
          <ReplaceButton onClick={() => fileInputRef.current?.click()}>
            {t("action.replaceImage")}
          </ReplaceButton>
          <HiddenInput
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageChange}
          />
        </ImageArea>
        <InfoRow $editing>
          <InfoLeft>
            <Label variant="soft" color="default" size="xSmall">
              #{floorPlan.floorNumber}
            </Label>
            <NameInput
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("card.namePlaceholder")}
            />
          </InfoLeft>
          <InfoRight>
            <TextButton onClick={handleCancel} disabled={isSaving}>{t("action.cancel")}</TextButton>
            <TextButton onClick={handleSave} disabled={isSaving}>{t("action.save")}</TextButton>
          </InfoRight>
        </InfoRow>
      </Card>
    );
  }

  return (
    <CardWrapper>
      {showLeftIndicator && <DropIndicator $position="left" />}
      <Card
        ref={cardRef}
        $reordering={reordering}
        $dragging={isDragging}
      >
        <ImageArea>
          <CardImage
            src={displayImageUrl || TEMP_IMAGE_URL}
            alt={floorPlan.name}
            draggable={false}
            $cover={!displayImageUrl || displayImageUrl === TEMP_IMAGE_URL}
          />
          {!reordering && (
            <ViewFullButton onClick={() => setLightboxOpen(true)}>
              <Icon icon="ic_fullsize2" width={16} height={16} />
            </ViewFullButton>
          )}
        </ImageArea>
        <ImageLightbox
          open={lightboxOpen}
          src={displayImageUrl || TEMP_IMAGE_URL}
          alt={floorPlan.name}
          onClose={() => setLightboxOpen(false)}
        />
        <InfoRow>
          <InfoLeft>
            <Label variant="soft" color="default" size="xSmall">
              #{floorPlan.floorNumber}
            </Label>
            <CardName>{floorPlan.name}</CardName>
          </InfoLeft>
          {!reordering && (
            <InfoRight>
              <IconButton onClick={handleStartEdit}>
                <Icon icon="ic_edit" width={16} height={16} />
              </IconButton>
              {deletable ? (
                <IconButton onClick={() => onDelete(floorPlan.id, floorPlan.name)}>
                  <IcDelete width={16} height={16} />
                </IconButton>
              ) : (
                <Tooltip
                  trigger={
                    <IconButton as="div" $disabled>
                      <IcDelete width={16} height={16} />
                    </IconButton>
                  }
                  side="top"
                  align="end"
                  withArrow
                >
                  <TooltipText>{t("tooltip.cannotDelete")}</TooltipText>
                </Tooltip>
              )}
            </InfoRight>
          )}
        </InfoRow>
      </Card>
      {showRightIndicator && <DropIndicator $position="right" />}
    </CardWrapper>
  );
}

// --- Empty/New card ---

interface NewFloorPlanCardProps {
  order: number;
  isSaving?: boolean;
  onSave: (data: { name: string; imageFile?: File }) => void;
  onCancel: () => void;
}

export function NewFloorPlanCard({ order, isSaving, onSave, onCancel }: NewFloorPlanCardProps) {
  const { t } = useTranslation("resource");
  const [name, setName] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);
  const [imageFile, setImageFile] = useState<File | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) {
      setImageFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSave = () => {
    onSave({ name, imageFile });
  };

  return (
    <Card $editing>
      <ImageArea
        $empty
        $editing
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
      >
        {previewUrl ? (
          <CardImage src={previewUrl} alt="" />
        ) : (
          <EmptyImageContent>
            <Button variant="outlined" color="primary" size="xSmall">
              {t("action.addImage")}
            </Button>
            <EmptyHint>{t("action.addImageHint")}</EmptyHint>
          </EmptyImageContent>
        )}
        <HiddenInput
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageChange}
        />
      </ImageArea>
      <InfoRow $editing>
        <InfoLeft>
          <Label variant="soft" color="default" size="xSmall">
            #{order}
          </Label>
          <NameInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("card.namePlaceholder")}
          />
        </InfoLeft>
        <InfoRight>
          <TextButton onClick={onCancel} disabled={isSaving}>{t("action.cancel")}</TextButton>
          <TextButton onClick={handleSave} disabled={!name.trim() || isSaving}>
            {t("action.save")}
          </TextButton>
        </InfoRight>
      </InfoRow>
    </Card>
  );
}

// --- Styles ---

const CardWrapper = styled.div`
  position: relative;
  display: flex;
  overflow: visible;
`;

const DropIndicator = styled.div<{ $position: "left" | "right" }>`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background-color: #484BD2;
  z-index: 10;
  pointer-events: none;

  ${({ $position }) =>
    $position === "left"
      ? css`left: 0; transform: translateX(-100%);`
      : css`right: 0; transform: translateX(100%);`}
`;

const Card = styled.div<{ $editing?: boolean; $reordering?: boolean; $dragging?: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  border-radius: 8px;
  border: 1px solid ${({ $editing }) => ($editing ? "#484BD2" : token("colors.border.outline"))};
  box-shadow: ${({ $editing }) => ($editing ? "0 0 0 1px #484BD2" : "none")};
  background: #fff;
  overflow: hidden;

  ${({ $reordering }) =>
    $reordering &&
    css`
      cursor: grab;
      user-select: none;
      transition: box-shadow 0.2s, opacity 0.2s;

      &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
      }
    `}

  ${({ $dragging }) =>
    $dragging &&
    css`
      opacity: 0.4;
      cursor: grabbing;
    `}
`;

const ImageArea = styled.div<{ $empty?: boolean; $editing?: boolean }>`
  position: relative;
  aspect-ratio: 299 / 168;
  border-bottom: 1px solid ${token("colors.border.outline")};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ $empty }) =>
    $empty &&
    css`
      background: ${token("colors.gray.GR50")};
      cursor: pointer;
    `}
`;

const CardImage = styled.img<{ $cover?: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: ${({ $cover }) => ($cover ? "cover" : "contain")};
  pointer-events: none;
  -webkit-user-drag: none;
`;

const EmptyImageContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const EmptyHint = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
  color: ${token("colors.gray.GR700")};
  text-align: center;
  white-space: pre-line;
`;

const ViewFullButton = styled.button`
  position: absolute;
  bottom: 8px;
  right: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border-radius: 3px;
  border: none;
  background: rgba(17, 17, 31, 0.32);
  color: #fff;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s;

  ${Card}:hover & {
    opacity: 1;
  }
`;

const ReplaceButton = styled.button`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 3px 8px;
  border-radius: 6px;
  border: none;
  background: rgba(17, 17, 31, 0.48);
  color: #fff;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
  cursor: pointer;
  box-shadow: 0px 20px 40px -4px rgba(105, 111, 140, 0.16);
`;

const InfoRow = styled.div<{ $editing?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 36px;
  padding: 0 8px;
  gap: 8px;
`;

const InfoLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const InfoRight = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;

  .tooltip-trigger-wrapper {
    display: inline-flex;
  }
`;

const CardName = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
  color: ${token("colors.text.primary")};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NameInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  border-bottom: 1px solid ${token("colors.border.outline")};
  outline: none;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.57;
  color: ${token("colors.text.primary")};
  background: transparent;
  padding: 0;

  &::placeholder {
    color: ${token("colors.text.disabled")};
  }
`;

const IconButton = styled.button<{ $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  color: ${({ $disabled }) => ($disabled ? "#8F95B2" : token("colors.text.primary"))};

  &:hover {
    color: ${({ $disabled }) => ($disabled ? "#8F95B2" : token("colors.text.secondary"))};
  }
`;

const TextButton = styled.button`
  padding: 3px 6px;
  border: none;
  border-radius: 6px;
  background: transparent;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
  color: ${token("colors.text.primary")};
  cursor: pointer;

  &:hover {
    background: ${token("colors.action.hover")};
  }

  &:disabled {
    color: rgba(143, 149, 178, 0.8);
    cursor: default;

    &:hover {
      background: transparent;
    }
  }
`;

const TooltipText = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 1;
  white-space: nowrap;
`;

const HiddenInput = styled.input`
  display: none;
`;
